import clsx from "clsx";
import { useAtom } from "jotai";
import {
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useState,
} from "react";
import useIsInViewport from "use-is-in-viewport";

import { validateAcceptedCpl } from "../../../shared/api/yenta-api";
import { showIntroModalAtom } from "../../../shared/components/arcade-intro-modal/arcade-intro-modal.state";
import { ArcadeTimer } from "../../../shared/components/arcade-timer.component";
import { cplEventsTracker } from "../../../shared/eventsTracker/events-tracker";
import { CplEventAction } from "../../../shared/eventsTracker/utils";
import {
  QUIZ_QUESTION_CHANGE_ANSWER_LIMIT_SECONDS,
  QUIZ_QUESTION_CORRECT_ANSWER_DELAY_SECONDS,
} from "../../widget.constants";
import {
  instructionsModalAtom,
  questionAtom,
  quizAtom,
} from "../../widget.state";
import {
  QuestionButtonProps,
  QuestionType,
  QuizQuestionProps,
} from "../../widget.types";
import { QuestionHeader } from "../question-header/question-header.component";
import { QuestionAds } from "../shared/question-ads.component";
import { QuestionButton } from "../shared/question-button.component";
import { QuestionCpl } from "../shared/question-cpl.component";
import { QuestionFeedback } from "../shared/question-feedback";
import { QuestionMedia } from "../shared/question-media.component";
import { OptInReminder } from "../shared/question-opt-in-reminder.component";
import { QuestionSms } from "../shared/question-sms.component";

export const QuizQuestion = forwardRef<HTMLDivElement, QuizQuestionProps>(
  (
    {
      id,
      questionIndex,
      currentQuestionIndex,
      showQuestionAds,
      onSetQuestionWithBanner,
      onDebounceAutoScroll,
      hasBanner,
      renderOptInReminder,
    },
    ref
  ) => {
    const [quiz] = useAtom(quizAtom);
    const [question, setQuestion] = useAtom(questionAtom(id));
    const isDisabled = questionIndex > currentQuestionIndex;
    const [isInViewport, wrappedTargetRef] = useIsInViewport({
      target: (ref as unknown) as MutableRefObject<HTMLElement>,
    });
    const [hasBeenPlayable, setHasBeenPlayable] = useState(false);
    const [showIntroModal] = useAtom(showIntroModalAtom);
    const [instructionsModal] = useAtom(instructionsModalAtom);
    const questionsPerPage = window.FKL.questionsPerPage;
    const isArcade = quiz.isArcade;

    const canClick = () => {
      onSetQuestionWithBanner(questionIndex);
      return !isDisabled;
    };

    const isQuestionAnswered = () => {
      return quiz.isArcade
        ? question.options.some((option) => option.isSelected)
        : question.options.some(
            (option) => option.isCorrect && option.isSelected
          );
    };

    const handleOptionClick = (id: string) => {
      if (!canClick() || isQuestionAnswered()) return;

      setQuestion((question) => ({
        ...question,
        options: question.options.map((option) => ({
          ...option,
          isSelected: option.id === id,
        })) as typeof question.options,
      }));
    };

    const handleCplClick = async (isAccepted: boolean) => {
      if (!canClick() || !question.options.some((option) => option.isSelected))
        return;

      const isCplRegistered = (q: QuestionType) =>
        question.cpl?.name === q.cpl?.name &&
        question.id != q.id &&
        q.cpl?.isAccepted;

      let registeredCpl: boolean = quiz.questions.some((q) =>
        isCplRegistered(q)
      );

      if (question.cpl?.formPath && isAccepted === false) {
        registeredCpl = await validateAcceptedCpl(
          question.cpl.formPath,
          question.cpl.emailReceiver
        );
      }

      if (isAccepted && question.cpl) {
        cplEventsTracker({
          action: CplEventAction.CLICK_YES,
          cplName: question.cpl.name,
          value: question.cpl.bid,
          email: question.cpl.emailReceiver,
        });
      } else if (!isAccepted && question.cpl) {
        cplEventsTracker({
          action: CplEventAction.CLICK_NO,
          cplName: question.cpl.name,
          value: question.cpl.bid,
          email: question.cpl.emailReceiver,
        });
      }

      setQuestion((question) => ({
        ...question,
        cpl: {
          ...question.cpl,
          isAccepted,
          isTacAccepted: isAccepted,
          registeredCpl,
        } as typeof question.cpl,
      }));
    };

    const handleTimeout = useCallback(() => {
      onSetQuestionWithBanner(null);
      setQuestion((question) => ({
        ...question,
        options: question.options.map((option) =>
          option.isCorrect ? option : { ...option, isSelected: true }
        ) as typeof question.options,
      }));
    }, [onSetQuestionWithBanner, setQuestion]);

    const isSmsQuestion =
      question.smsInfo?.brandName &&
      question.smsInfo.question &&
      question.smsInfo.smsBrandPrivacyPolicy &&
      question.smsInfo.tacSmsBrand;

    const isSingleQuestionQuestion = !(
      (question.smsInfo?.brandName && isSmsQuestion) ||
      question.cpl?.title ||
      renderOptInReminder
    );

    const isQuestionDisabled = isDisabled || isQuestionAnswered();

    const waitAutoNextPage = useCallback(() => {
      return isSingleQuestionQuestion && !isQuestionDisabled
        ? QUIZ_QUESTION_CHANGE_ANSWER_LIMIT_SECONDS
        : isDisabled
        ? 0
        : isSingleQuestionQuestion && isQuestionDisabled
        ? QUIZ_QUESTION_CORRECT_ANSWER_DELAY_SECONDS
        : 0;
    }, [isDisabled, isQuestionDisabled, isSingleQuestionQuestion]);

    useEffect(() => {
      if (isInViewport && !isDisabled) setHasBeenPlayable(true);
    }, [isDisabled, isInViewport]);

    useEffect(() => {
      if (hasBanner && currentQuestionIndex !== questionIndex) {
        onDebounceAutoScroll!(waitAutoNextPage());
      }
    }, [
      hasBanner,
      currentQuestionIndex,
      questionIndex,
      onDebounceAutoScroll,
      waitAutoNextPage,
    ]);

    return (
      <>
        <div>
          {(!isArcade || questionIndex === 0) && (
            <QuestionHeader
              questionBrand={question.brandName ?? ""}
              isArcade={quiz.isArcade}
            />
          )}
          <div
            ref={wrappedTargetRef}
            className={clsx(
              "quiz--single-page__body__question-v2",
              isArcade && "arcade-question__body"
            )}
            data-testid="quiz-question"
            data-id={id}
          >
            <div className="quiz--single-page__body__container">
              <QuestionMedia
                {...question.media}
                isActive={questionIndex <= currentQuestionIndex}
              />

              <div className="quiz--single-page__body__question__container">
                {/* Question title & options */}
                <div className="quiz--single-page__body__question__section">
                  <div className="quiz--single-page__body__question__section__header">
                    <div className="quiz--single-page__body__question__section__header__entries">
                      +1
                    </div>
                    <div
                      className="quiz--single-page__body__question__section__header__title"
                      data-testid="quiz-question-title"
                    >
                      {questionIndex + 1}. {question.title}
                    </div>
                    {quiz.isArcade &&
                      hasBeenPlayable &&
                      currentQuestionIndex === questionIndex &&
                      !question.options.some((option) => option.isSelected) && (
                        <ArcadeTimer
                          isRunning={!showIntroModal && !instructionsModal}
                          time={20}
                          onTimeout={handleTimeout}
                        />
                      )}
                  </div>
                  <div className="quiz--single-page__body__question__section__body question__section__body--quiz-question">
                    <div className="quiz__buttons">
                      {question.options.map((option) => {
                        const buttonProps: QuestionButtonProps = {
                          isSelected: option.isSelected,
                          isCorrect: option.isCorrect,
                          isSms: false,
                          isCpl: false,
                          isReminder: false,
                          isDisabled: isQuestionDisabled,
                          onClick: () => handleOptionClick(option.id),
                        };
                        return (
                          <QuestionButton
                            key={option.id}
                            data-testid="quiz-question-option"
                            data-option-id={option.id}
                            {...buttonProps}
                          >
                            {option.title}
                          </QuestionButton>
                        );
                      })}
                    </div>
                    {!isArcade &&
                      question.options.some((option) => option.isSelected) && (
                        <QuestionFeedback
                          isCorrect={question.options.some(
                            (option) => option.isSelected && option.isCorrect
                          )}
                          isSingleQuestionQuestion={isSingleQuestionQuestion}
                        />
                      )}
                  </div>
                </div>

                {/* Question SMS */}
                {question.smsInfo?.brandName && isSmsQuestion && (
                  <QuestionSms
                    {...question.smsInfo}
                    isDisabled={
                      isDisabled || !question.options.some((o) => o.isSelected)
                    }
                    setQuestion={setQuestion}
                    canClick={canClick}
                  />
                )}

                {/* CPL */}
                {question.cpl?.title && (
                  <QuestionCpl
                    {...question.cpl}
                    isDisabled={
                      isDisabled || !question.options.some((o) => o.isSelected)
                    }
                    onCplClick={handleCplClick}
                  />
                )}

                {/* Opt-in reminder */}
                {renderOptInReminder && (
                  <OptInReminder
                    isQuestionAnswered={question.options.some(
                      (option) => option.isSelected
                    )}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ads when more than 1 question per page */}
        {showQuestionAds &&
          questionIndex <= currentQuestionIndex &&
          questionsPerPage > 1 && <QuestionAds questionIndex={questionIndex} />}
      </>
    );
  }
);
